/**
 * LineItemsTable — multi-row repeater for the composer.
 *
 * One row per invoice line. Columns mirror the prototype's S12 grid:
 *   description · HSN · qty · unit · rate · disc% · tax · amount · ✕
 *
 * Inputs are controlled (paise + millis stored as bigint/number on
 * the parent's state). The amount column is a read-only display of
 * the per-line `lineTotalPaise` from the gst-calc shim — the parent
 * recomputes on every change and passes the per-row computed totals
 * down so this table only formats them.
 *
 * Why a plain JSX table instead of a generic data-grid: the row
 * shape is fixed (8 inputs + 1 read-only), the volume is small (≤50
 * lines per invoice in practice), and the column-specific
 * affordances (mono inputs for money, % suffix on disc, dropdown for
 * unit/tax) wouldn't fit a generic grid cleanly.
 *
 * Pure presentation — no autosave, no validation. The parent
 * (`composer-page.tsx`) owns the form state and the persistence.
 *
 * @see docs/spec/11-new-invoice-screen-spec.md §6 — line items.
 */
import { Trash2, Plus } from "lucide-react";
import { Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@flint/ui";
import { formatPaise, parseRupeesToPaise } from "./lib/format-money.ts";
import type { ShimLineComputed, ShimLineForeignComputed } from "./lib/gst-calc-shim.ts";
import { ItemPicker } from "./item-picker.tsx";

export interface ComposerLineRow {
  /** Stable key for React reconciliation. Generated by the parent. */
  key: string;
  /** Linked item selected from the catalogue; null for free-text lines. */
  itemId: string | null;
  description: string;
  hsn: string;
  qtyMilli: number;
  unit: string;
  ratePaise: bigint;
  /**
   * Foreign-currency rate (ADR-0020). Present iff the composer is in a
   * non-INR currency. The parent owns the keystroke → minor-unit
   * conversion; this table just renders the input in the chosen currency.
   */
  rateForeignMinor: bigint | null;
  discountBps: number;
  taxRateBps: number;
}

export interface LineItemsTableProps {
  rows: ComposerLineRow[];
  /** Per-row computed INR totals from the gst-calc shim (parallel index). */
  computed: ShimLineComputed[];
  /**
   * Per-row computed foreign-minor totals. Present iff `currencyCode !== 'INR'`.
   * When provided, the Rate + Amount columns render in invoice currency.
   * ADR-0020.
   */
  computedForeign?: ShimLineForeignComputed[];
  /**
   * Invoice currency code. Drives the Rate column header symbol and the
   * Amount column's value source (foreign vs INR). Defaults to `'INR'`.
   */
  currencyCode?: string;
  onChange: (key: string, patch: Partial<ComposerLineRow>) => void;
  onRemove: (key: string) => void;
  onAdd: () => void;
  /**
   * Compact summary rendered inside the tfoot row, to the right of
   * "Add line item" — mirrors the prototype's S12 layout where the
   * "3 items · ₹93,500 taxable · IGST 18%" line sits in the table
   * footer rather than as a separate paragraph below the table.
  */
  summary?: React.ReactNode;
  /** Placeholder for the description input. Defaults to the invoice composer copy. */
  descriptionPlaceholder?: string;
  /** Placeholder for the rate input. Useful for proforma QA hooks. */
  ratePlaceholder?: string;
  /** Add-row button label. Defaults to the invoice composer copy. */
  addLabel?: string;
  disabled?: boolean;
}

/** Three-letter currency code → symbol used in column headers + amount cells. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: "₹",
  USD: "$",
  AED: "د.إ",
  EUR: "€",
  GBP: "£",
  SGD: "S$",
  AUD: "A$",
  CAD: "C$",
  JPY: "¥",
};
function symbolFor(code: string): string {
  return CURRENCY_SYMBOLS[code] ?? code;
}

// Standard Indian GST slabs surfaced on a line. 0% is included for
// nil-rated / exempt / export lines — when the invoice-level tax
// treatment is one of the zero-rated values (nil_rated, exempt,
// out_of_scope, export_lut, sez_supply) the composer auto-snaps every
// line to 0 (see `composer-page.tsx` Tax-treatment select), and this
// list lets the dropdown render the resulting selection without
// falling back to the placeholder.
//
// 18% stays the composer's per-line default (`composer-page.tsx`,
// `defaultLine()`) because that's the dominant SME rate. Operators
// shift down to 0% / 5% / 12% per item when the goods category
// demands it (Zoho / QuickBooks / Tally use the same pattern).
const TAX_OPTIONS = [
  { value: "0", label: "0%" },
  { value: "500", label: "5%" },
  { value: "1200", label: "12%" },
  { value: "1800", label: "18%" },
  { value: "2800", label: "28%" },
];

const UNIT_OPTIONS = ["Nos", "pcs", "kg", "m", "box", "set", "hr"].map((u) => ({
  value: u,
  label: u,
}));

export function LineItemsTable({
  rows,
  computed,
  computedForeign,
  currencyCode = "INR",
  onChange,
  onRemove,
  onAdd,
  summary,
  descriptionPlaceholder = "Pick item or type description...",
  ratePlaceholder,
  addLabel = "Add line item",
  disabled,
}: LineItemsTableProps) {
  const isForeign = currencyCode !== "INR";
  const sym = symbolFor(currencyCode);
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full text-sm min-w-[820px]">
        <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
          <tr>
            <th className="px-3 py-2 text-left font-medium" scope="col">
              Description
            </th>
            <th className="px-2 py-2 text-left font-medium w-[80px]" scope="col">
              HSN
            </th>
            <th className="px-2 py-2 text-right font-medium w-[80px]" scope="col">
              Qty
            </th>
            <th className="px-2 py-2 text-left font-medium w-[80px]" scope="col">
              Unit
            </th>
            <th className="px-2 py-2 text-right font-medium w-[110px]" scope="col">
              Rate ({sym})
            </th>
            <th className="px-2 py-2 text-right font-medium w-[70px]" scope="col">
              Disc%
            </th>
            <th className="px-2 py-2 text-left font-medium w-[100px]" scope="col">
              Tax
            </th>
            <th className="px-2 py-2 text-right font-medium w-[120px]" scope="col">
              Amount ({sym})
            </th>
            <th className="px-2 py-2 w-[40px]" scope="col">
              <span className="sr-only">Remove</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const total = computed[i];
            const totalForeign = computedForeign?.[i];
            return (
              <tr key={row.key} className="border-t border-line">
                <td className="px-2 py-2">
                  <div className="flex items-center gap-1">
                    <ItemPicker
                      disabled={disabled}
                      onPick={(picked) =>
                        onChange(row.key, {
                          itemId: picked.itemId,
                          description: picked.description,
                          hsn: picked.hsn,
                          unit: picked.unit,
                          // The item master is INR-only — the operator
                          // can edit the foreign rate column after if
                          // they're billing in another currency.
                          ratePaise: picked.ratePaise,
                          taxRateBps: picked.taxRateBps,
                        })
                      }
                    />
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) =>
                        onChange(row.key, { itemId: null, description: e.target.value })
                      }
                      placeholder={descriptionPlaceholder}
                      aria-label="Line item description"
                      disabled={disabled}
                      className="flex-1 bg-transparent text-sm text-ink-2 px-1 py-1 rounded-lg focus:outline-none focus:bg-raised focus:ring-1 focus:ring-line"
                    />
                  </div>
                </td>
                <td className="px-1 py-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={row.hsn}
                    onChange={(e) =>
                      onChange(row.key, { hsn: e.target.value.replace(/\D/g, "").slice(0, 8) })
                    }
                    placeholder="0000"
                    aria-label="HSN code"
                    disabled={disabled}
                    className="w-full bg-transparent text-sm font-mono text-ink-2 px-1 py-1 rounded-lg focus:outline-none focus:bg-raised focus:ring-1 focus:ring-line"
                  />
                </td>
                <td className="px-1 py-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={millisToText(row.qtyMilli)}
                    onChange={(e) => {
                      const m = textToMillis(e.target.value);
                      if (m !== null) onChange(row.key, { qtyMilli: m });
                    }}
                    aria-label="Quantity"
                    disabled={disabled}
                    className="w-full text-right bg-transparent text-sm font-mono text-ink-2 px-1 py-1 rounded-lg focus:outline-none focus:bg-raised focus:ring-1 focus:ring-line"
                  />
                </td>
                <td className="px-1 py-2">
                  <Select value={row.unit} onValueChange={(v) => onChange(row.key, { unit: v })}>
                    <SelectTrigger className="h-8 rounded-lg text-sm" aria-label="Unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-1 py-2">
                  {isForeign ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      value={minorToInputText(row.rateForeignMinor ?? 0n)}
                      onChange={(e) => {
                        const m = parseMajorToMinor(e.target.value);
                        if (m !== null) onChange(row.key, { rateForeignMinor: m });
                      }}
                      aria-label="Rate"
                      placeholder={ratePlaceholder ?? `Rate ${sym}`}
                      disabled={disabled}
                      className="w-full text-right bg-transparent text-sm font-mono text-ink-2 px-1 py-1 rounded-lg focus:outline-none focus:bg-raised focus:ring-1 focus:ring-line"
                    />
                  ) : (
                    <input
                      type="text"
                      inputMode="decimal"
                      value={paiseToInputText(row.ratePaise)}
                      onChange={(e) => {
                        const p = parseRupeesToPaise(e.target.value);
                        if (p !== null) onChange(row.key, { ratePaise: p });
                      }}
                      aria-label="Rate"
                      placeholder={ratePlaceholder ?? `Rate ${sym}`}
                      disabled={disabled}
                      className="w-full text-right bg-transparent text-sm font-mono text-ink-2 px-1 py-1 rounded-lg focus:outline-none focus:bg-raised focus:ring-1 focus:ring-line"
                    />
                  )}
                </td>
                <td className="px-1 py-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bpsToPercentText(row.discountBps)}
                    onChange={(e) => {
                      const bps = percentTextToBps(e.target.value);
                      if (bps !== null) onChange(row.key, { discountBps: bps });
                    }}
                    aria-label="Discount percent"
                    disabled={disabled}
                    className="w-full text-right bg-transparent text-sm font-mono text-ink-2 px-1 py-1 rounded-lg focus:outline-none focus:bg-raised focus:ring-1 focus:ring-line"
                  />
                </td>
                <td className="px-1 py-2">
                  <Select
                    value={String(row.taxRateBps)}
                    onValueChange={(v) => onChange(row.key, { taxRateBps: Number(v) })}
                  >
                    <SelectTrigger className="h-8 rounded-lg text-sm" aria-label="GST slab">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TAX_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2 text-right font-mono text-sm text-ink">
                  {isForeign
                    ? totalForeign
                      ? `${sym}${minorToInputText(totalForeign.lineTotalForeignMinor)}`
                      : "—"
                    : total
                      ? formatPaise(total.lineTotalPaise)
                      : "—"}
                </td>
                <td className="px-1 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => onRemove(row.key)}
                    disabled={disabled || rows.length === 1}
                    className="text-muted hover:text-bad disabled:opacity-30 disabled:cursor-not-allowed p-1"
                    aria-label="Remove line"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-line bg-raised">
            <td colSpan={9} className="px-2 py-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button type="button" intent="ghost" size="sm" onClick={onAdd} disabled={disabled}>
                  <Plus size={14} />
                  <span>{addLabel}</span>
                </Button>
                {summary ? <span className="text-xs text-muted">{summary}</span> : null}
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── input ↔ paise/millis/bps helpers ────────────────────────────────

function millisToText(milli: number): string {
  if (milli === 0) return "";
  const whole = Math.floor(milli / 1000);
  const frac = milli % 1000;
  if (frac === 0) return String(whole);
  return `${whole}.${String(frac).padStart(3, "0").replace(/0+$/, "")}`;
}

function textToMillis(text: string): number | null {
  const cleaned = text.replace(/[\s,]/g, "").trim();
  if (cleaned === "") return 0;
  if (!/^\d*(\.\d{0,3})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ""] = cleaned.split(".");
  const padded = (fracPart + "000").slice(0, 3);
  return Number.parseInt(intPart || "0", 10) * 1000 + Number.parseInt(padded || "0", 10);
}

function paiseToInputText(paise: bigint): string {
  if (paise === 0n) return "";
  const rupees = paise / 100n;
  const remainder = paise % 100n;
  if (remainder === 0n) return rupees.toString();
  return `${rupees}.${String(remainder).padStart(2, "0")}`;
}

function bpsToPercentText(bps: number): string {
  if (bps === 0) return "";
  const whole = Math.floor(bps / 100);
  const frac = bps % 100;
  if (frac === 0) return String(whole);
  return `${whole}.${String(frac).padStart(2, "0").replace(/0+$/, "")}`;
}

function percentTextToBps(text: string): number | null {
  const cleaned = text.replace(/[\s,%]/g, "").trim();
  if (cleaned === "") return 0;
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ""] = cleaned.split(".");
  const padded = (fracPart + "00").slice(0, 2);
  const bps = Number.parseInt(intPart || "0", 10) * 100 + Number.parseInt(padded || "0", 10);
  return Math.min(bps, 10_000);
}

/** Foreign-currency minor → "1234.56" string. Same shape as paiseToInputText. */
function minorToInputText(minor: bigint): string {
  if (minor === 0n) return "";
  const sign = minor < 0n ? "-" : "";
  const abs = minor < 0n ? -minor : minor;
  const major = abs / 100n;
  const frac = abs % 100n;
  if (frac === 0n) return `${sign}${major}`;
  return `${sign}${major}.${frac.toString().padStart(2, "0")}`;
}

/** "1234.56" → minor units (12345..). Returns null on invalid input. */
function parseMajorToMinor(text: string): bigint | null {
  const cleaned = text.replace(/[\s,]/g, "").trim();
  if (cleaned === "") return 0n;
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ""] = cleaned.split(".");
  const padded = (fracPart + "00").slice(0, 2);
  return BigInt(intPart || "0") * 100n + BigInt(padded || "0");
}
